function defaultFormatter(value) {
  return value;
}
function formatters(...fns) {
  return (value) => {
    let result = value;
    for (const fn of fns) {
      result = fn(result);
    }
    return result;
  };
}
function call(fn, ...args) {
  if (fn.length > 0 && args.length > fn.length) {
    return fn(...args.slice(0, fn.length));
  }
  return fn(...args);
}
function ensureList(param) {
  if (!param) return [];
  if (Array.isArray(param)) return param;
  return [param];
}
function ensureDict(param, defaultValue, defaultKey = null) {
  if (!param) {
    param = defaultValue;
  }
  if (typeof param !== "object" || param === null || Array.isArray(param)) {
    if (param) {
      defaultValue = param;
    }
    const key = defaultKey ?? void 0;
    return [{ [key]: param }, defaultValue];
  }
  const asObj = param;
  return [asObj, defaultValue];
}
function filterIndex(collection, predicate, index) {
  if (typeof predicate === "number") {
    index = predicate;
    predicate = null;
  }
  let result = predicate ? collection.filter(predicate) : [...collection];
  if (index !== null && index !== void 0) {
    if (index < 0) return result[result.length + index];
    return result[index];
  }
  return result;
}
function setDefaults(defaults, kwargs, override = false) {
  if ("clear" in defaults && defaults["clear"]) {
    for (const key of Object.keys(kwargs)) delete kwargs[key];
    delete defaults["clear"];
  }
  for (const [key, value] of Object.entries(defaults)) {
    if (key in kwargs) {
      if (Array.isArray(value) && Array.isArray(kwargs[key])) {
        kwargs[key] = [...value, ...kwargs[key]];
        continue;
      }
      if (value && typeof value === "object" && !Array.isArray(value) && kwargs[key] && typeof kwargs[key] === "object" && !Array.isArray(kwargs[key])) {
        setDefaults(value, kwargs[key]);
        continue;
      }
    }
    if (!(key in kwargs) || override) {
      kwargs[key] = value;
    }
  }
}
function isIterable(obj) {
  if (obj === null || obj === void 0) return false;
  if (typeof obj === "string") return false;
  return typeof obj[Symbol.iterator] === "function";
}
function* findAll(string, sub, start = 0, end, ignoreCase = false) {
  let haystack = string;
  let needle = sub;
  if (ignoreCase) {
    haystack = haystack.toLowerCase();
    needle = needle.toLowerCase();
  }
  const limit = end !== void 0 ? end : haystack.length;
  let idx = start;
  while (true) {
    const found = haystack.indexOf(needle, idx);
    if (found === -1 || found >= limit) return;
    yield found;
    idx = found + needle.length;
  }
}
function getFirstDefined(data, keys, defaultValue = void 0) {
  for (const key of keys) {
    if (key !== null && key !== void 0 && key in data) {
      return data[key];
    }
  }
  return defaultValue;
}
function extendSafe(target, source) {
  for (const elt of source) {
    if (!target.includes(elt)) {
      target.push(elt);
    }
  }
}
class IdentitySet {
  _map = /* @__PURE__ */ new Map();
  constructor(items) {
    if (items) {
      for (const item of items) this.add(item);
    }
  }
  add(value) {
    this._map.set(value, true);
    return this;
  }
  delete(value) {
    return this._map.delete(value);
  }
  has(value) {
    return this._map.has(value);
  }
  get size() {
    return this._map.size;
  }
  [Symbol.iterator]() {
    return this._map.keys();
  }
}
function alwaysTrue(_match) {
  return true;
}
function charsBefore(chars, match) {
  if (!match.inputString) return true;
  const idx = match.start - 1;
  if (idx < 0) return true;
  return chars.includes(match.inputString[idx]);
}
function charsAfter(chars, match) {
  if (!match.inputString) return true;
  const idx = match.end;
  if (idx >= match.inputString.length) return true;
  return chars.includes(match.inputString[idx]);
}
function charsSurround(chars, match) {
  return charsBefore(chars, match) && charsAfter(chars, match);
}
function validators(...chainedValidators) {
  return (match) => chainedValidators.every((v) => v(match));
}
let DEBUG = false;
let LOG_LEVEL = 10;
function frameRepr(frame) {
  const basename = frame.filename.split("/").pop() ?? frame.filename;
  return `${basename}#L${frame.lineno}`;
}
function definedAt() {
  if (!DEBUG) return void 0;
  const err = new Error();
  const stack = err.stack;
  if (!stack) return void 0;
  const lines = stack.split("\n").slice(1);
  for (const line of lines) {
    if (line.includes("/src/") && (line.includes("/rebulk-js/src/") || line.includes("/rebulk-js/dist/"))) {
      continue;
    }
    const match = line.match(/at\s+(?:(.+?)\s+)?\(?(.+?):(\d+):\d+\)?/);
    if (match) {
      return {
        lineno: parseInt(match[3], 10),
        package_: void 0,
        name: match[1] || void 0,
        filename: match[2]
      };
    }
  }
  return void 0;
}
function setDebug(value) {
  DEBUG = value;
}
function setLogLevel(value) {
  LOG_LEVEL = value;
}
class Match {
  start;
  end;
  name;
  tags;
  marker;
  private;
  inputString;
  formatter;
  pattern;
  parent;
  conflictSolver;
  defined_at;
  /** Index within the sequence of matches produced by the same pattern. */
  matchIndex = 0;
  _value;
  _children;
  _rawStart;
  _rawEnd;
  constructor(start, end, opts = {}) {
    this.start = start;
    this.end = end;
    this.name = opts.name;
    this.tags = ensureList(opts.tags);
    this.marker = opts.marker ?? false;
    this.private = opts.private ?? false;
    this.inputString = opts.inputString;
    this.formatter = opts.formatter;
    this._value = opts.value;
    this.conflictSolver = opts.conflictSolver;
    this.pattern = opts.pattern;
    this.parent = opts.parent;
    this.defined_at = opts.pattern ? opts.pattern.defined_at : definedAt();
  }
  get span() {
    return [this.start, this.end];
  }
  get rawStart() {
    return this._rawStart !== void 0 ? this._rawStart : this.start;
  }
  set rawStart(v) {
    this._rawStart = v;
  }
  get rawEnd() {
    return this._rawEnd !== void 0 ? this._rawEnd : this.end;
  }
  set rawEnd(v) {
    this._rawEnd = v;
  }
  get raw() {
    if (this.inputString !== void 0) {
      return this.inputString.slice(this.rawStart, this.rawEnd);
    }
    return void 0;
  }
  get value() {
    if (this._value) return this._value;
    if (this.formatter && typeof this.formatter === "function") return this.formatter(this.raw ?? "");
    return this.raw;
  }
  set value(v) {
    this._value = v;
  }
  get children() {
    if (!this._children) {
      this._children = new Matches(void 0, this.inputString);
    }
    return this._children;
  }
  set children(v) {
    this._children = v;
  }
  /** All property names in this match (recursing into children). */
  get names() {
    if (!this._children || this._children.length === 0) {
      return this.name ? /* @__PURE__ */ new Set([this.name]) : /* @__PURE__ */ new Set();
    }
    const ret = /* @__PURE__ */ new Set();
    for (const child of this._children) {
      for (const n of child.names) ret.add(n);
    }
    return ret;
  }
  /** Walk up to the root (top-most parent). */
  get initiator() {
    let m = this;
    while (m.parent) m = m.parent;
    return m;
  }
  get length() {
    return this.end - this.start;
  }
  /** Check if this match has at least one of the given tags. */
  tagged(...tags) {
    return tags.some((t) => this.tags.includes(t));
  }
  /** Check if any child (recursive) has one of the given names. */
  named(...names) {
    return names.some((n) => this.names.has(n));
  }
  /**
   * Crop this match with the given spans/matches.
   * Returns a list of Match fragments after subtracting the crop regions.
   */
  crop(crops, predicate, index) {
    const cropsArr = Array.isArray(crops) && crops.length === 2 && typeof crops[0] === "number" ? [crops] : Array.isArray(crops) ? crops : [crops];
    const initial = this._clone();
    let ret = [initial];
    for (const crop of cropsArr) {
      const [cStart, cEnd] = "span" in crop ? crop.span : crop;
      for (const current of [...ret]) {
        if (cStart <= current.start && cEnd >= current.end) {
          ret.splice(ret.indexOf(current), 1);
        } else if (cStart >= current.start && cEnd <= current.end) {
          const right = current._clone();
          current.end = cStart;
          if (current.length === 0) ret.splice(ret.indexOf(current), 1);
          right.start = cEnd;
          if (right.length > 0) ret.push(right);
        } else if (current.end >= cEnd && cEnd > current.start) {
          current.start = cEnd;
        } else if (current.start <= cStart && cStart < current.end) {
          current.end = cStart;
        }
      }
    }
    return filterIndex(ret, predicate ?? null, index ?? null);
  }
  /**
   * Split this match into multiple matches at each separator character.
   */
  split(seps, predicate, index) {
    const ret = [];
    let currentStart = null;
    for (let i = 0; i < (this.raw?.length ?? 0); i++) {
      const char = this.raw[i];
      if (seps.includes(char)) {
        if (currentStart !== null) {
          const m = this._clone();
          m.start = this.start + currentStart;
          m.end = this.start + i;
          ret.push(m);
          currentStart = null;
        }
      } else {
        if (currentStart === null) currentStart = i;
      }
    }
    if (currentStart !== null) {
      const m = this._clone();
      m.start = this.start + currentStart;
      ret.push(m);
    }
    return filterIndex(ret, predicate ?? null, index ?? null);
  }
  clone() {
    return this._clone();
  }
  _clone() {
    const m = new Match(this.start, this.end, {
      name: this.name,
      tags: [...this.tags],
      marker: this.marker,
      private: this.private,
      inputString: this.inputString,
      formatter: this.formatter,
      value: this._value,
      conflictSolver: this.conflictSolver,
      pattern: this.pattern,
      parent: this.parent
    });
    m.matchIndex = this.matchIndex;
    m._rawStart = this._rawStart;
    m._rawEnd = this._rawEnd;
    return m;
  }
  /** Equality by span, value, name, parent identity. */
  equals(other) {
    return this.start === other.start && this.end === other.end && this.value === other.value && this.name === other.name && this.parent === other.parent;
  }
  toString() {
    let flags = "";
    const initiatorVal = this.initiator.value;
    if (initiatorVal !== this.value) flags += `+initiator=${String(initiatorVal)}`;
    if (this.private) flags += "+private";
    const name = this.name ? `+name=${this.name}` : "";
    const tags = this.tags.length ? `+tags=${JSON.stringify(this.tags)}` : "";
    const defined = this.defined_at ? `@${this.defined_at.filename.split("/").pop()}#L${this.defined_at.lineno}` : "";
    return `<${String(this.value)}:(${this.start}, ${this.end})${flags}${name}${tags}${defined}>`;
  }
}
class MatchesDict extends Map {
  /** Keyed by property name → all Match objects for that name. */
  matches = /* @__PURE__ */ new Map();
  /** Keyed by property name → unique values (de-duped). */
  valuesList = /* @__PURE__ */ new Map();
}
class _BaseMatches {
  inputString;
  _delegate = [];
  _maxEnd = 0;
  // Lazy lookup caches (null = not yet built)
  _nameDict = null;
  _tagDict = null;
  _startDict = null;
  _endDict = null;
  _indexDict = null;
  constructor(matches, inputString) {
    this.inputString = inputString;
    if (matches) this.extend(matches);
  }
  get length() {
    return this._delegate.length;
  }
  [Symbol.iterator]() {
    return this._delegate[Symbol.iterator]();
  }
  toArray() {
    return [...this._delegate];
  }
  /** All matches as an array. Ported Python code reads `matches.matches` — without
   *  this getter that silently evaluated to undefined and turned rules into no-ops. */
  get matches() {
    return [...this._delegate];
  }
  // ── Cache accessors ──────────────────────────────────────────────────────
  get nameDict() {
    if (!this._nameDict) {
      this._nameDict = /* @__PURE__ */ new Map();
      for (const m of this._delegate) {
        if (m.name) {
          const arr = this._nameDict.get(m.name) ?? [];
          arr.push(m);
          this._nameDict.set(m.name, arr);
        }
      }
    }
    return this._nameDict;
  }
  get startDict() {
    if (!this._startDict) {
      this._startDict = /* @__PURE__ */ new Map();
      for (const m of this._delegate) {
        const arr = this._startDict.get(m.start) ?? [];
        arr.push(m);
        this._startDict.set(m.start, arr);
      }
    }
    return this._startDict;
  }
  get endDict() {
    if (!this._endDict) {
      this._endDict = /* @__PURE__ */ new Map();
      for (const m of this._delegate) {
        const arr = this._endDict.get(m.end) ?? [];
        arr.push(m);
        this._endDict.set(m.end, arr);
      }
    }
    return this._endDict;
  }
  get tagDict() {
    if (!this._tagDict) {
      this._tagDict = /* @__PURE__ */ new Map();
      for (const m of this._delegate) {
        for (const tag of m.tags) {
          const arr = this._tagDict.get(tag) ?? [];
          arr.push(m);
          this._tagDict.set(tag, arr);
        }
      }
    }
    return this._tagDict;
  }
  get indexDict() {
    if (!this._indexDict) {
      this._indexDict = /* @__PURE__ */ new Map();
      for (const m of this._delegate) {
        for (let i = m.start; i < m.end; i++) {
          const arr = this._indexDict.get(i) ?? [];
          arr.push(m);
          this._indexDict.set(i, arr);
        }
      }
    }
    return this._indexDict;
  }
  get maxEnd() {
    return this.inputString ? Math.max(this.inputString.length, this._maxEnd) : this._maxEnd;
  }
  // ── Mutations ─────────────────────────────────────────────────────────────
  _addMatch(match) {
    if (this._nameDict && match.name) {
      const arr = this._nameDict.get(match.name) ?? [];
      arr.push(match);
      this._nameDict.set(match.name, arr);
    }
    if (this._tagDict) {
      for (const tag of match.tags) {
        const arr = this._tagDict.get(tag) ?? [];
        arr.push(match);
        this._tagDict.set(tag, arr);
      }
    }
    if (this._startDict) {
      const arr = this._startDict.get(match.start) ?? [];
      arr.push(match);
      this._startDict.set(match.start, arr);
    }
    if (this._endDict) {
      const arr = this._endDict.get(match.end) ?? [];
      arr.push(match);
      this._endDict.set(match.end, arr);
    }
    if (this._indexDict) {
      for (let i = match.start; i < match.end; i++) {
        const arr = this._indexDict.get(i) ?? [];
        arr.push(match);
        this._indexDict.set(i, arr);
      }
    }
    if (match.end > this._maxEnd) this._maxEnd = match.end;
  }
  _removeMatch(match) {
    if (this._nameDict && match.name) {
      const arr = this._nameDict.get(match.name);
      if (arr) {
        const idx = arr.findIndex((m) => m === match);
        if (idx !== -1) arr.splice(idx, 1);
      }
    }
    if (this._tagDict) {
      for (const tag of match.tags) {
        const arr = this._tagDict.get(tag);
        if (arr) {
          const idx = arr.findIndex((m) => m === match);
          if (idx !== -1) arr.splice(idx, 1);
        }
      }
    }
    if (this._startDict) {
      const arr = this._startDict.get(match.start);
      if (arr) {
        const idx = arr.findIndex((m) => m === match);
        if (idx !== -1) arr.splice(idx, 1);
      }
    }
    if (this._endDict) {
      const arr = this._endDict.get(match.end);
      if (arr) {
        const idx = arr.findIndex((m) => m === match);
        if (idx !== -1) arr.splice(idx, 1);
      }
    }
    if (this._indexDict) {
      for (let i = match.start; i < match.end; i++) {
        const arr = this._indexDict.get(i);
        if (arr) {
          const idx = arr.findIndex((m) => m === match);
          if (idx !== -1) arr.splice(idx, 1);
        }
      }
    }
    if (match.end >= this._maxEnd) {
      this._maxEnd = 0;
      for (const m of this._delegate) {
        if (m.end > this._maxEnd) this._maxEnd = m.end;
      }
    }
  }
  append(match) {
    this._delegate.push(match);
    this._addMatch(match);
  }
  extend(matches) {
    for (const m of matches) this.append(m);
  }
  remove(match) {
    const idx = this._delegate.findIndex((m) => m === match);
    if (idx !== -1) {
      this._delegate.splice(idx, 1);
      this._removeMatch(match);
    }
  }
  includes(match) {
    return this._delegate.some((m) => m === match || m.equals(match));
  }
  at(index) {
    return this._delegate[index];
  }
  get(index) {
    return this._delegate[index];
  }
  clear() {
    this._delegate.length = 0;
    this._nameDict = null;
    this._tagDict = null;
    this._startDict = null;
    this._endDict = null;
    this._indexDict = null;
    this._maxEnd = 0;
  }
  slice(start, end) {
    return new Matches(this._delegate.slice(start, end), this.inputString);
  }
  deleteSlice(start, end) {
    const removed = this._delegate.splice(start, end - start);
    for (const m of removed) this._removeMatch(m);
  }
  setSlice(start, end, ...items) {
    const removed = this._delegate.splice(start, end - start, ...items);
    for (const m of removed) this._removeMatch(m);
    for (const m of items) this._addMatch(m);
  }
  setAt(index, match) {
    const old = this._delegate[index];
    if (old) this._removeMatch(old);
    this._delegate[index] = match;
    this._addMatch(match);
  }
  insert(index, match) {
    this._delegate.splice(index, 0, match);
    this._addMatch(match);
  }
  sort() {
    return [...this._delegate].sort((a, b) => {
      if (a.start !== b.start) return a.start - b.start;
      return a.end - b.end;
    });
  }
  named(name, predicate, index) {
    const arr = this.nameDict.get(name) ?? [];
    return filterIndex(arr, predicate, index);
  }
  tagged(tag, predicate, index) {
    const arr = this.tagDict.get(tag) ?? [];
    return filterIndex(arr, predicate, index);
  }
  starting(start, predicate, index) {
    const arr = this.startDict.get(start) ?? [];
    return filterIndex(arr, predicate, index);
  }
  ending(end, predicate, index) {
    const arr = this.endDict.get(end) ?? [];
    return filterIndex(arr, predicate, index);
  }
  atIndex(pos, predicate, index) {
    const arr = this.indexDict.get(pos) ?? [];
    return filterIndex(arr, predicate, index);
  }
  atSpan(span, predicate, index) {
    const starting = this.indexDict.get(span[0]) ?? [];
    const ending = this.indexDict.get(span[1] - 1) ?? [];
    const merged = [...starting];
    for (const m of ending) {
      if (!merged.includes(m)) merged.push(m);
    }
    return filterIndex(merged, predicate, index);
  }
  atMatch(match, predicate, index) {
    return this.atSpan(match.span, predicate, index);
  }
  range(start = 0, end, predicate, index) {
    const limit = end !== void 0 ? Math.min(this.maxEnd, end) : this.maxEnd;
    const ret = [];
    for (const m of this.sort()) {
      if (m.start < limit && m.end > start) {
        if (!predicate || predicate(m)) ret.push(m);
      }
    }
    return filterIndex(ret, null, index);
  }
  /** Nearest match ending just before (or at) `match.start`. */
  previous(match, predicate, index) {
    let current = match.start;
    while (current > -1) {
      const prev = this.ending(current);
      if (prev.length > 0) return filterIndex(prev, predicate, index);
      current--;
    }
    return filterIndex([], predicate, index);
  }
  /** Nearest match starting after `match.start`. */
  next(match, predicate, index) {
    let current = match.start + 1;
    while (current <= this.maxEnd) {
      const nxt = this.starting(current);
      if (nxt.length > 0) return filterIndex(nxt, predicate, index);
      current++;
    }
    return filterIndex([], predicate, index);
  }
  /** All matches that overlap with `match`. */
  conflicting(match, predicate, index) {
    const ret = [];
    const seen = /* @__PURE__ */ new Set();
    for (let i = match.start; i < match.end; i++) {
      for (const m of this.indexDict.get(i) ?? []) {
        if (m !== match && !seen.has(m)) {
          seen.add(m);
          ret.push(m);
        }
      }
    }
    return filterIndex(ret, predicate, index);
  }
  /** Matches chained before `position` separated only by chars in `seps`. */
  chainBefore(position, seps, start = 0, predicate, index) {
    const pos = typeof position === "number" ? position : position.start;
    const chain = [];
    for (let i = Math.min(this.maxEnd, pos) - 1; i >= start; i--) {
      const atI = this.indexDict.get(i) ?? [];
      const filtered = predicate ? atI.filter(predicate) : [...atI];
      if (filtered.length > 0) {
        for (const m of filtered) {
          if (!chain.includes(m)) chain.push(m);
        }
      } else if (!this.inputString || !seps.includes(this.inputString[i])) {
        break;
      }
    }
    return filterIndex(chain, null, index);
  }
  /** Matches chained after `position` separated only by chars in `seps`. */
  chainAfter(position, seps, end, predicate, index) {
    const pos = typeof position === "number" ? position : position.end;
    const limit = end !== void 0 ? Math.min(this.maxEnd, end) : this.maxEnd;
    const chain = [];
    for (let i = pos; i < limit; i++) {
      const atI = this.indexDict.get(i) ?? [];
      const filtered = predicate ? atI.filter(predicate) : [...atI];
      if (filtered.length > 0) {
        for (const m of filtered) {
          if (!chain.includes(m)) chain.push(m);
        }
      } else if (!this.inputString || !seps.includes(this.inputString[i])) {
        break;
      }
    }
    return filterIndex(chain, null, index);
  }
  /** All "hole" matches (gaps not covered by any existing match). */
  holes(start = 0, end, opts = {}) {
    const { formatter, ignore, seps, predicate, index } = opts;
    const limit = end !== void 0 ? Math.min(this.maxEnd, end) : this.maxEnd;
    const ret = [];
    let hole = false;
    let loopStart = 0;
    for (let i = start - 1; i >= 0; i--) {
      const startingMatches = this.starting(i);
      let found = false;
      for (const m of startingMatches) {
        if (!ignore || !ignore(m)) {
          loopStart = i;
          found = true;
          break;
        }
      }
      if (found) break;
    }
    for (let rindex = loopStart; rindex < limit; rindex++) {
      const atI = this.indexDict.get(rindex) ?? [];
      const current = ignore ? atI.filter((m) => !ignore(m)) : [...atI];
      if (seps && hole && this.inputString && seps.includes(this.inputString[rindex])) {
        hole = false;
        ret[ret.length - 1].end = rindex;
      } else {
        if (current.length === 0 && !hole) {
          hole = true;
          ret.push(new Match(Math.max(rindex, start), 0, { inputString: this.inputString, formatter }));
        } else if (current.length > 0 && hole) {
          hole = false;
          ret[ret.length - 1].end = rindex;
        }
      }
    }
    if (ret.length > 0 && hole) {
      let holeEnd = this.maxEnd;
      const lastRindex = limit > loopStart ? limit - 1 : loopStart;
      for (let ri = lastRindex; ri < this.maxEnd; ri++) {
        const startingMatches = this.starting(ri);
        let found = false;
        for (const m of startingMatches) {
          if (!ignore || !ignore(m)) {
            holeEnd = ri;
            found = true;
            break;
          }
        }
        if (found) break;
      }
      ret[ret.length - 1].end = Math.min(holeEnd, limit);
    }
    const validHoles = ret.filter((h) => h.end > h.start);
    return filterIndex(validHoles, predicate ?? null, index ?? null);
  }
  /** All property names present. */
  get names() {
    return new Set(this.nameDict.keys());
  }
  /** All tags present. */
  get allTags() {
    return new Set(this.tagDict.keys());
  }
  /**
   * Convert to a plain dict (like Python Matches.to_dict).
   */
  toDict(details = false, firstValue = false, enforceList = false) {
    const ret = new MatchesDict();
    for (const match of this.sort()) {
      const val = details ? match : match.value;
      const matchArr = ret.matches.get(match.name ?? "") ?? [];
      matchArr.push(match);
      ret.matches.set(match.name ?? "", matchArr);
      const valEquals = (a, b) => details && a instanceof Match && b instanceof Match ? a.equals(b) : a === b;
      if (!enforceList) {
        const valArr = ret.valuesList.get(match.name ?? "") ?? [];
        if (!valArr.some((v) => valEquals(v, val))) valArr.push(val);
        ret.valuesList.set(match.name ?? "", valArr);
      }
      const existing = ret.get(match.name ?? "");
      if (existing !== void 0) {
        if (!firstValue) {
          if (Array.isArray(existing)) {
            if (!existing.some((v) => valEquals(v, val))) existing.push(val);
          } else {
            if (!valEquals(existing, val)) ret.set(match.name, [existing, val]);
          }
        }
      } else {
        ret.set(match.name, enforceList && !Array.isArray(val) ? [val] : val);
      }
    }
    return ret;
  }
  toString() {
    return `[${this._delegate.map(String).join(", ")}]`;
  }
}
class Matches extends _BaseMatches {
  markers;
  constructor(matches, inputString) {
    super(void 0, inputString);
    this.markers = new Markers(void 0, inputString);
    if (matches) this.extend(matches);
  }
  _addMatch(match) {
    if (match.marker) throw new Error("A marker match should not be added to Matches");
    super._addMatch(match);
  }
}
class Markers extends _BaseMatches {
  constructor(matches, inputString) {
    super(void 0, inputString);
    if (matches) this.extend(matches);
  }
  _addMatch(match) {
    if (!match.marker) throw new Error("A non-marker match should not be added to Markers");
    super._addMatch(match);
  }
}
function filterMatchKwargs(opts, children = false) {
  const result = { ...opts };
  delete result["pattern"];
  delete result["start"];
  delete result["end"];
  delete result["parent"];
  delete result["formatter"];
  delete result["value"];
  if (children) {
    delete result["name"];
  }
  return result;
}
function convertPythonNamedGroups(pattern) {
  return pattern.replace(/\(\?P</g, "(?<");
}
function applyAbbreviations(pattern, abbreviations) {
  let result = pattern;
  for (const [key, replacement] of abbreviations) {
    result = result.split(key).join(replacement);
  }
  return result;
}
function compileRegex(source, opts) {
  let src = convertPythonNamedGroups(source);
  if (opts.abbreviations) {
    src = applyAbbreviations(src, opts.abbreviations);
  }
  let flagStr = opts.flags ?? "";
  if (opts.ignoreCase && !flagStr.includes("i")) flagStr += "i";
  if (!flagStr.includes("g")) flagStr += "g";
  if (!flagStr.includes("d")) flagStr += "d";
  return new RegExp(src, flagStr);
}
function buildGroupNames(source) {
  const names = [];
  let i = 0;
  while (i < source.length) {
    if (source[i] === "\\") {
      i += 2;
      continue;
    }
    if (source[i] === "[") {
      i++;
      while (i < source.length && source[i] !== "]") {
        if (source[i] === "\\") i++;
        i++;
      }
      i++;
      continue;
    }
    if (source[i] === "(") {
      const next = source[i + 1];
      if (next !== "?") {
        names.push(void 0);
      } else {
        const next2 = source[i + 2];
        if (next2 === "<") {
          const next3 = source[i + 3];
          if (next3 !== "=" && next3 !== "!") {
            const nameEnd = source.indexOf(">", i + 3);
            names.push(nameEnd !== -1 ? source.slice(i + 3, nameEnd) : void 0);
          }
        }
      }
    }
    i++;
  }
  return names;
}
class BasePattern {
}
class Pattern extends BasePattern {
  name;
  tags;
  formatters;
  defaultFormatterFn;
  values;
  defaultValue;
  validators;
  defaultValidator;
  children;
  every;
  private_;
  privateNames;
  ignoreNames;
  privateParent;
  privateChildren;
  marker;
  formatAll;
  validateAll;
  disabled;
  logLevel;
  properties_;
  postProcessor;
  preMatchProcessor;
  postMatchProcessor;
  defined_at;
  _opts;
  constructor(opts = {}) {
    super();
    this._opts = opts;
    this.name = opts.name;
    this.tags = ensureList(opts.tags);
    const [formattersMap, defaultFmt] = ensureDict(opts.formatter, defaultFormatter);
    this.formatters = formattersMap;
    this.defaultFormatterFn = defaultFmt ?? defaultFormatter;
    const [valuesMap, defaultVal] = ensureDict(opts.value, void 0);
    this.values = valuesMap;
    this.defaultValue = defaultVal;
    const [validatorsMap, defaultValid] = ensureDict(opts.validator, alwaysTrue);
    this.validators = validatorsMap;
    this.defaultValidator = defaultValid ?? alwaysTrue;
    this.every = opts.every ?? false;
    this.children = opts.children ?? false;
    this.private_ = opts.private ?? false;
    this.privateNames = opts.privateNames ?? [];
    this.ignoreNames = opts.ignoreNames ?? [];
    this.privateParent = opts.privateParent ?? false;
    this.privateChildren = opts.privateChildren ?? false;
    this.marker = opts.marker ?? false;
    this.formatAll = opts.formatAll ?? false;
    this.validateAll = opts.validateAll ?? false;
    const d = opts.disabled;
    if (typeof d === "function") {
      this.disabled = d;
    } else if (typeof d === "boolean") {
      this.disabled = () => d;
    } else {
      this.disabled = () => false;
    }
    this.logLevel = opts.logLevel ?? 0;
    this.properties_ = opts.properties;
    this.postProcessor = typeof opts.postProcessor === "function" ? opts.postProcessor : void 0;
    this.preMatchProcessor = typeof opts.preMatchProcessor === "function" ? opts.preMatchProcessor : void 0;
    this.postMatchProcessor = typeof opts.postMatchProcessor === "function" ? opts.postMatchProcessor : void 0;
    this.defined_at = definedAt();
  }
  get shouldIncludeChildren() {
    return this.children || this.every;
  }
  get shouldIncludeParent() {
    return !this.children || this.every;
  }
  _matchConfigPropertyKeys(match, child = false) {
    const keys = [];
    if (match.name) keys.push(match.name);
    keys.push(child ? "__children__" : "__parent__");
    keys.push(null);
    return keys;
  }
  _processMatchPrivate(match, child = false) {
    if (match.name && this.privateNames.includes(match.name) || !child && this.privateParent || child && this.privateChildren) {
      match.private = true;
    }
  }
  _processMatchValue(match, child = false) {
    const keys = this._matchConfigPropertyKeys(match, child);
    const patternValue = getFirstDefined(this.values, keys, this.defaultValue);
    if (patternValue !== void 0 && patternValue !== null) {
      match.value = patternValue;
    }
  }
  _processMatchFormatter(match, child = false) {
    const included = child ? this.shouldIncludeChildren : this.shouldIncludeParent;
    if (included || this.formatAll) {
      const keys = this._matchConfigPropertyKeys(match, child);
      match.formatter = getFirstDefined(this.formatters, keys, this.defaultFormatterFn);
    }
  }
  _processMatchValidator(match, child = false) {
    const included = child ? this.shouldIncludeChildren : this.shouldIncludeParent;
    if (included || this.validateAll) {
      const keys = this._matchConfigPropertyKeys(match, child);
      const validator = getFirstDefined(this.validators, keys, this.defaultValidator);
      if (validator && !validator(match)) return false;
    }
    return true;
  }
  _processMatch(match, matchIndex, child = false) {
    match.matchIndex = matchIndex;
    this._processMatchPrivate(match, child);
    this._processMatchValue(match, child);
    this._processMatchFormatter(match, child);
    return this._processMatchValidator(match, child);
  }
  static _applyProcessor(match, processor) {
    if (!processor) return match;
    if (!match) return match;
    const ret = processor(match);
    return ret !== void 0 ? ret : match;
  }
  *_processMatches(match, matchIndex) {
    const processed = Pattern._applyProcessor(match, this.preMatchProcessor);
    if (!processed) return;
    if (!this._processMatch(processed, matchIndex)) return;
    for (const child of processed.children) {
      if (!this._processMatch(child, matchIndex, true)) return;
    }
    const postProcessed = Pattern._applyProcessor(processed, this.postMatchProcessor);
    if (!postProcessed) return;
    if ((this.shouldIncludeParent || this.privateParent) && !this.ignoreNames.includes(postProcessed.name ?? "")) {
      yield postProcessed;
    }
    if (this.shouldIncludeChildren || this.privateChildren) {
      for (const child of postProcessed.children) {
        if (!this.ignoreNames.includes(child.name ?? "")) yield child;
      }
    }
  }
  _postProcessMatches(matches) {
    if (this.postProcessor) return this.postProcessor(matches, this);
    return matches;
  }
  matches(inputString, context, withRawMatches = false) {
    const allMatches = [];
    const rawMatches = [];
    for (const pattern of this.patterns) {
      let matchIndex = 0;
      for (const rawMatch of this._match(pattern, inputString, context)) {
        rawMatches.push(rawMatch);
        for (const m of this._processMatches(rawMatch, matchIndex)) {
          allMatches.push(m);
        }
        matchIndex++;
      }
    }
    const finalMatches = this._postProcessMatches(allMatches);
    if (withRawMatches) return [finalMatches, rawMatches];
    return finalMatches;
  }
  get properties() {
    return this.properties_ ?? {};
  }
}
class StringPattern extends Pattern {
  _patterns;
  _matchKwargs;
  constructor(firstArg, ...rest) {
    if (typeof firstArg === "string") {
      super({});
      this._patterns = [firstArg, ...rest];
      this._matchKwargs = filterMatchKwargs({});
    } else {
      super(firstArg);
      this._patterns = rest;
      this._matchKwargs = filterMatchKwargs(firstArg);
    }
  }
  get patterns() {
    return this._patterns;
  }
  get matchOptions() {
    return this._matchKwargs;
  }
  *_match(pattern, inputString, _context) {
    const ignoreCase = (this._opts.ignoreCase ?? false) || (this._opts.flags?.includes("i") ?? false);
    const searchStart = this._opts.start ?? 0;
    const searchEnd = this._opts.end ?? void 0;
    for (const idx of findAll(inputString, pattern, searchStart, searchEnd, ignoreCase)) {
      const match = new Match(idx, idx + pattern.length, {
        ...this._matchKwargs,
        pattern: this,
        inputString
      });
      if (match.length > 0) yield match;
    }
  }
  toString() {
    return `<StringPattern:(${this._patterns.map((p) => `'${p}'`).join(", ")})>`;
  }
}
class RePattern extends Pattern {
  _regexes;
  _matchKwargs;
  _childrenMatchKwargs;
  _groupNamesList;
  constructor(opts, ...patterns) {
    super(opts);
    this._matchKwargs = filterMatchKwargs(opts);
    this._childrenMatchKwargs = filterMatchKwargs(opts, true);
    this._regexes = [];
    this._groupNamesList = [];
    for (const p of patterns) {
      if (p instanceof RegExp) {
        this._regexes.push(p);
        this._groupNamesList.push(buildGroupNames(p.source));
      } else {
        const compiled = compileRegex(p, {
          flags: opts.flags,
          ignoreCase: opts.ignoreCase,
          abbreviations: opts.abbreviations
        });
        this._regexes.push(compiled);
        this._groupNamesList.push(buildGroupNames(compiled.source));
      }
    }
  }
  get patterns() {
    return this._regexes;
  }
  get matchOptions() {
    return this._matchKwargs;
  }
  *_match(pattern, inputString, _context) {
    const groupNames = this._groupNamesList[this._regexes.indexOf(pattern)] ?? [];
    pattern.lastIndex = 0;
    let m;
    while ((m = pattern.exec(inputString)) !== null) {
      const start = m.index;
      const end = m.index + m[0].length;
      const mainMatch = new Match(start, end, {
        ...this._matchKwargs,
        pattern: this,
        inputString
      });
      if (groupNames.length > 0) {
        const indices = m.indices;
        for (let i = 0; i < groupNames.length; i++) {
          const groupIdx = i + 1;
          const name = groupNames[i] ?? this.name;
          const groupIndices = indices?.[groupIdx];
          if (groupIndices && groupIndices[0] !== -1) {
            const [gs, ge] = groupIndices;
            const childMatch = new Match(gs, ge, {
              ...this._childrenMatchKwargs,
              name,
              parent: mainMatch,
              pattern: this,
              inputString
            });
            if (childMatch.length > 0) mainMatch.children.append(childMatch);
          }
        }
      }
      if (mainMatch.length > 0 || m[0].length === 0) {
        if (mainMatch.length > 0) yield mainMatch;
      }
      if (m[0].length === 0) pattern.lastIndex++;
    }
  }
  toString() {
    return `<RePattern:(${this._regexes.map((r) => r.source).join(", ")})>`;
  }
}
class FunctionalPattern extends Pattern {
  _fns;
  _matchKwargs;
  constructor(opts, ...fns) {
    super(opts);
    this._fns = fns;
    this._matchKwargs = filterMatchKwargs(opts);
  }
  get patterns() {
    return this._fns;
  }
  get matchOptions() {
    return this._matchKwargs;
  }
  *_match(fn, inputString, context) {
    const ret = fn(inputString, context);
    if (!ret) return;
    const isSingleResult = (v) => {
      if (!Array.isArray(v)) return true;
      if (v.length >= 2 && typeof v[0] === "number" && typeof v[1] === "number") return true;
      return false;
    };
    const args_iterable = isSingleResult(ret) ? [ret] : ret;
    for (const args of args_iterable) {
      if (!args) continue;
      if (typeof args === "object" && !Array.isArray(args)) {
        const { start: s, end: e, ...rest } = args;
        const opts = { ...this._matchKwargs, ...rest };
        const m = new Match(s ?? 0, e ?? 0, {
          ...opts,
          pattern: this,
          inputString
        });
        if (m.length > 0) yield m;
      } else if (Array.isArray(args)) {
        let matchOpts = this._matchKwargs;
        let start, end;
        if (args.length >= 3 && typeof args[2] === "object" && !Array.isArray(args[2])) {
          matchOpts = { ...this._matchKwargs, ...args[2] };
          [start, end] = args;
        } else {
          [start, end] = args;
        }
        const m = new Match(start, end, { ...matchOpts, pattern: this, inputString });
        if (m.length > 0) yield m;
      }
    }
  }
  toString() {
    return `<FunctionalPattern:(${this._fns.map((f) => f.name || "anonymous").join(", ")})>`;
  }
}
let _ChainCtor = null;
function registerChain(ctor) {
  _ChainCtor = ctor;
}
function getChainClass() {
  return _ChainCtor;
}
function splitPatternsOpts(args) {
  if (args.length === 0) return [[], {}];
  const first = args[0];
  const last = args[args.length - 1];
  if (typeof first === "object" && first !== null) {
    return [args.slice(1), first];
  }
  if (args.length > 1 && typeof last === "object" && last !== null) {
    return [args.slice(0, -1), last];
  }
  return [args, {}];
}
class Builder {
  _defaults = {};
  _regexDefaults = {};
  _stringDefaults = {};
  _functionalDefaults = {};
  _chainDefaults = {};
  reset() {
    this._defaults = {};
    this._regexDefaults = {};
    this._stringDefaults = {};
    this._functionalDefaults = {};
    this._chainDefaults = {};
    return this;
  }
  defaults(kwargs) {
    setDefaults(kwargs, this._defaults, true);
    return this;
  }
  regexDefaults(kwargs) {
    setDefaults(kwargs, this._regexDefaults, true);
    return this;
  }
  stringDefaults(kwargs) {
    setDefaults(kwargs, this._stringDefaults, true);
    return this;
  }
  functionalDefaults(kwargs) {
    setDefaults(kwargs, this._functionalDefaults, true);
    return this;
  }
  chainDefaults(kwargs) {
    setDefaults(kwargs, this._chainDefaults, true);
    return this;
  }
  _applyOverrides(kwargs) {
    const overrideKeys = kwargs["overrides"] ?? [];
    delete kwargs["overrides"];
    const backup = {};
    for (const k of overrideKeys) backup[k] = kwargs[k];
    return [kwargs, backup];
  }
  buildRe(opts, ...patterns) {
    const kwargs = { ...opts };
    const [, backup] = this._applyOverrides(kwargs);
    setDefaults(this._regexDefaults, kwargs);
    setDefaults(this._defaults, kwargs);
    Object.assign(kwargs, backup);
    return new RePattern(kwargs, ...patterns);
  }
  buildString(opts, ...patterns) {
    const kwargs = { ...opts };
    const [, backup] = this._applyOverrides(kwargs);
    setDefaults(this._stringDefaults, kwargs);
    setDefaults(this._defaults, kwargs);
    Object.assign(kwargs, backup);
    return new StringPattern(kwargs, ...patterns);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buildFunctional(opts, ...fns) {
    const kwargs = { ...opts };
    const [, backup] = this._applyOverrides(kwargs);
    setDefaults(this._functionalDefaults, kwargs);
    setDefaults(this._defaults, kwargs);
    Object.assign(kwargs, backup);
    return new FunctionalPattern(kwargs, ...fns);
  }
  /**
   * Fluent API: add a regex pattern and return `this`.
   * Supports both:
   *   regex(opts, 'pat1', 'pat2')  — options-first
   *   regex('pat1', 'pat2', opts)  — patterns-first, opts last
   *   regex('pat1', 'pat2')        — patterns only
   */
  regex(...args) {
    const [patterns, opts] = splitPatternsOpts(args);
    const p = this.buildRe(opts, ...patterns);
    return this.pattern(p);
  }
  /**
   * Fluent API: add a string pattern and return `this`.
   */
  string(...args) {
    const [patterns, opts] = splitPatternsOpts(args);
    const p = this.buildString(opts, ...patterns);
    return this.pattern(p);
  }
  /**
   * Fluent API: add a functional pattern and return `this`.
   * Supports:
   *   functional(fn)                — function only
   *   functional(fn, opts)          — fn-first, opts-last (Python style)
   *   functional(opts, fn)          — opts-first, fn-last
   *   functional(opts, fn1, fn2, …) — opts-first, multiple fns
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  functional(opts, ...fns) {
    let p;
    if (typeof opts === "function") {
      const lastArg = fns[fns.length - 1];
      if (fns.length > 0 && typeof lastArg === "object" && lastArg !== null && !Array.isArray(lastArg)) {
        const o = lastArg;
        const actualFns = fns.slice(0, -1);
        p = this.buildFunctional(o, opts, ...actualFns);
      } else {
        p = this.buildFunctional({}, opts, ...fns);
      }
    } else {
      p = this.buildFunctional(opts, ...fns);
    }
    return this.pattern(p);
  }
  /**
   * Fluent API: start a chain and return it.
   * The chain is also registered via pattern().
   */
  chain(opts = {}) {
    const ChainCtor = getChainClass();
    if (!ChainCtor) throw new Error("Chain class not registered. Import chain.ts before calling chain().");
    const kwargs = { ...opts };
    setDefaults(this._chainDefaults, kwargs);
    setDefaults(this._defaults, kwargs);
    const c = new ChainCtor(this, kwargs);
    c._defaults = { ...this._defaults };
    c._regexDefaults = { ...this._regexDefaults };
    c._stringDefaults = { ...this._stringDefaults };
    c._functionalDefaults = { ...this._functionalDefaults };
    c._chainDefaults = { ...this._chainDefaults };
    this.pattern(c);
    return c;
  }
}
class CyclicDependency extends Error {
  cyclic;
  constructor(cyclic) {
    const parts = [...cyclic.entries()].map(([k, v]) => `${String(k)} -> ${[...v].map(String).join(", ")}`);
    super(`Cyclic dependencies exist among these items: ${parts.join("; ")}`);
    this.cyclic = cyclic;
    this.name = "CyclicDependency";
  }
}
function toposortFlatten(data, sort = true) {
  const result = [];
  for (const group of toposort(data)) {
    const items = [...group];
    if (sort) items.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
    result.push(...items);
  }
  return result;
}
function* toposort(data) {
  if (data.size === 0) return;
  const workData = /* @__PURE__ */ new Map();
  for (const [k, v] of data) {
    const deps = new Set(v);
    deps.delete(k);
    workData.set(k, deps);
  }
  const allDeps = /* @__PURE__ */ new Set();
  for (const deps of workData.values()) {
    for (const d of deps) allDeps.add(d);
  }
  for (const dep of allDeps) {
    if (!workData.has(dep)) workData.set(dep, /* @__PURE__ */ new Set());
  }
  while (workData.size > 0) {
    const ordered = /* @__PURE__ */ new Set();
    for (const [item, deps] of workData) {
      if (deps.size === 0) ordered.add(item);
    }
    if (ordered.size === 0) {
      throw new CyclicDependency(workData);
    }
    yield ordered;
    for (const item of ordered) workData.delete(item);
    for (const deps of workData.values()) {
      for (const item of ordered) deps.delete(item);
    }
  }
}
class Consequence {
}
class Condition {
}
class CustomRule extends Condition {
  static priority = 0;
  static ruleName = void 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static dependency = null;
  static properties = {};
  priority = this.constructor.priority;
  name_ = this.constructor.name;
  dependency_ = this.constructor.dependency;
  logLevel = 0;
  defined_at;
  constructor() {
    super();
    this.defined_at = definedAt();
  }
  enabled(_context) {
    return true;
  }
  toString() {
    const defined = this.defined_at ? `@${this.defined_at.filename.split("/").pop()}#L${this.defined_at.lineno}` : "";
    return `<${this.name_ ?? this.constructor.name}${defined}>`;
  }
  /** Equality: same class = same rule (for deduplication). */
  equals(other) {
    return this.constructor === other.constructor;
  }
}
class Rule extends CustomRule {
  consequence = null;
  then(matches, whenResponse, context) {
    const cons = this.consequence ?? this.constructor.consequence;
    if (Array.isArray(cons)) {
      const responses = Array.isArray(whenResponse) ? whenResponse : [whenResponse];
      const iter = responses[Symbol.iterator]();
      for (const c of cons) {
        const instance = typeof c === "function" ? new c() : c;
        instance.then(matches, iter.next().value, context);
      }
    } else {
      const instance = typeof cons === "function" ? new cons() : cons;
      if (instance) instance.then(matches, whenResponse, context);
    }
  }
}
class RemoveMatch extends Consequence {
  then(matches, whenResponse, _context) {
    if (!whenResponse) return void 0;
    if (isIterable(whenResponse) && !(whenResponse instanceof String)) {
      const ret = [];
      for (const m2 of whenResponse) {
        if (matches.includes(m2)) {
          matches.remove(m2);
          ret.push(m2);
        }
      }
      return ret;
    }
    const m = whenResponse;
    if (matches.includes(m)) {
      matches.remove(m);
      return m;
    }
    return void 0;
  }
}
class AppendMatch extends Consequence {
  matchName;
  constructor(matchName) {
    super();
    this.matchName = matchName;
  }
  then(matches, whenResponse, _context) {
    if (!whenResponse) return void 0;
    if (isIterable(whenResponse) && !(whenResponse instanceof String)) {
      const ret = [];
      for (const m2 of whenResponse) {
        if (!matches.includes(m2)) {
          if (this.matchName) m2.name = this.matchName;
          matches.append(m2);
          ret.push(m2);
        }
      }
      return ret;
    }
    const m = whenResponse;
    if (this.matchName) m.name = this.matchName;
    if (!matches.includes(m)) {
      matches.append(m);
      return m;
    }
    return void 0;
  }
}
class RenameMatch extends Consequence {
  matchName;
  _remove = new RemoveMatch();
  _append = new AppendMatch();
  constructor(matchName) {
    super();
    this.matchName = matchName;
  }
  then(matches, whenResponse, context) {
    const removed = this._remove.then(matches, whenResponse, context);
    if (!removed) return;
    if (Array.isArray(removed)) {
      for (const m of removed) m.name = this.matchName;
      this._append.then(matches, removed, context);
    } else {
      removed.name = this.matchName;
      this._append.then(matches, removed, context);
    }
  }
}
class AppendTags extends Consequence {
  tags;
  _remove = new RemoveMatch();
  _append = new AppendMatch();
  constructor(tags) {
    super();
    this.tags = tags;
  }
  then(matches, whenResponse, context) {
    const removed = this._remove.then(matches, whenResponse, context);
    if (!removed) return;
    if (Array.isArray(removed)) {
      for (const m of removed) m.tags.push(...this.tags);
    } else {
      removed.tags.push(...this.tags);
    }
    this._append.then(matches, removed, context);
  }
}
class RemoveTags extends Consequence {
  tags;
  _remove = new RemoveMatch();
  _append = new AppendMatch();
  constructor(tags) {
    super();
    this.tags = tags;
  }
  then(matches, whenResponse, context) {
    const removed = this._remove.then(matches, whenResponse, context);
    if (!removed) return;
    const allRemoved = Array.isArray(removed) ? removed : [removed];
    for (const m of allRemoved) {
      for (const tag of this.tags) {
        const idx = m.tags.indexOf(tag);
        if (idx !== -1) m.tags.splice(idx, 1);
      }
    }
    this._append.then(matches, removed, context);
  }
}
class Rules {
  _list = [];
  constructor(...rules) {
    this.load(...rules);
  }
  load(...rules) {
    for (const rule of rules) {
      if (typeof rule === "function" && rule.prototype instanceof CustomRule) {
        this._list.push(new rule());
      } else if (typeof rule === "object" && rule !== null && !(rule instanceof CustomRule)) {
        this.loadModule(rule);
      } else if (rule instanceof CustomRule) {
        this._list.push(rule);
      } else if (typeof rule === "function") {
        this._list.push(new rule());
      }
    }
  }
  /**
   * Load rules from a module-like object by scanning its exported members.
   * Port of Python Rules.load_module() which uses inspect.getmembers().
   */
  loadModule(module) {
    for (const value of Object.values(module)) {
      if (typeof value === "function" && value.prototype instanceof CustomRule) {
        this._list.push(new value());
      }
    }
  }
  extend(other) {
    for (const r of other._list) {
      if (!this._list.some((existing) => existing.equals(r))) {
        this._list.push(r);
      }
    }
  }
  indexOf(rule) {
    return this._list.indexOf(rule);
  }
  [Symbol.iterator]() {
    return this._list[Symbol.iterator]();
  }
  get length() {
    return this._list.length;
  }
  executeAllRules(matches, context) {
    const ret = [];
    const byPriority = /* @__PURE__ */ new Map();
    for (const rule of this._list) {
      const p = rule.priority;
      const arr = byPriority.get(p) ?? [];
      arr.push(rule);
      byPriority.set(p, arr);
    }
    const sortedPriorities = [...byPriority.keys()].sort((a, b) => b - a);
    for (const _priority of sortedPriorities) {
      const priorityRules = byPriority.get(_priority);
      const sorted = toposortRules(priorityRules);
      for (const ruleGroup of sorted) {
        const groupArr = [...ruleGroup].sort((a, b) => this.indexOf(a) - this.indexOf(b));
        for (const rule of groupArr) {
          const whenResponse = executeRule(rule, matches, context);
          if (whenResponse !== null && whenResponse !== void 0 && whenResponse !== false) {
            ret.push([rule, whenResponse]);
          }
        }
      }
    }
    return ret;
  }
}
function executeRule(rule, matches, context) {
  if (!rule.enabled(context)) return null;
  const whenResponse = rule.when(matches, context);
  if (whenResponse !== null && whenResponse !== void 0 && whenResponse !== false) {
    rule.then(matches, whenResponse, context);
    return whenResponse;
  }
  return null;
}
function toposortRules(rules) {
  const graph = /* @__PURE__ */ new Map();
  const classToDeps = /* @__PURE__ */ new Map();
  for (const rule of rules) {
    const cls = rule.constructor;
    classToDeps.set(cls, [...classToDeps.get(cls) ?? [], rule]);
  }
  for (const rule of rules) {
    const deps = /* @__PURE__ */ new Set();
    const rawDeps = rule.dependency_;
    if (rawDeps) {
      const depArr = Array.isArray(rawDeps) ? rawDeps : [rawDeps];
      for (const dep of depArr) {
        let resolved = [];
        if (typeof dep === "string") {
          for (const [cls, insts] of classToDeps) {
            if (cls.name === dep) {
              resolved = insts;
              break;
            }
          }
        } else {
          resolved = classToDeps.get(dep) ?? [];
        }
        for (const inst of resolved) {
          if (inst !== rule) deps.add(inst);
        }
      }
    }
    graph.set(rule, deps);
  }
  return [...toposort(graph)];
}
const DEFAULT_SYMBOL = "__default__";
const PRE_PROCESS = 2048;
const POST_PROCESS = -2048;
function defaultConflictSolver(match, conflicting) {
  if (conflicting.initiator.length < match.initiator.length) return conflicting;
  if (match.initiator.length < conflicting.initiator.length) return match;
  return null;
}
class ConflictSolver extends Rule {
  static priority = PRE_PROCESS;
  priority = PRE_PROCESS;
  consequence = RemoveMatch;
  get defaultConflictSolverFn() {
    return defaultConflictSolver;
  }
  when(matches, _context) {
    const toRemove = new IdentitySet();
    const publicMatches = matches.toArray().filter((m) => !m.private).sort((a, b) => a.length - b.length);
    for (const match of publicMatches) {
      const conflicting = matches.conflicting(match) ?? [];
      if (!conflicting.length) continue;
      const publicConflicting = conflicting.filter((c) => !c.private).sort((a, b) => a.length - b.length);
      for (const conflictingMatch of publicConflicting) {
        const solvers = [
          [this.defaultConflictSolverFn, false]
        ];
        if (match.conflictSolver) solvers.push([match.conflictSolver, false]);
        if (conflictingMatch.conflictSolver) solvers.push([conflictingMatch.conflictSolver, true]);
        for (let si = solvers.length - 1; si >= 0; si--) {
          const [solver, reverse] = solvers[si];
          const toRem = reverse ? solver(conflictingMatch, match) : solver(match, conflictingMatch);
          if (toRem === DEFAULT_SYMBOL) continue;
          if (toRem && !toRemove.has(toRem)) {
            const toKeep = toRem === match ? conflictingMatch : match;
            if (!toRemove.has(toKeep)) {
              toRemove.add(toRem);
            }
          }
          break;
        }
      }
    }
    return toRemove;
  }
}
class PrivateRemover extends Rule {
  static priority = POST_PROCESS;
  priority = POST_PROCESS;
  consequence = RemoveMatch;
  when(matches, _context) {
    return matches.toArray().filter((m) => m.private);
  }
}
class InvalidChainException extends Error {
  constructor() {
    super("Invalid chain");
  }
}
class ChainPart extends BasePattern {
  _chain;
  pattern;
  repeaterStart = 1;
  repeaterEnd = 1;
  _hidden = false;
  constructor(chain, pattern) {
    super();
    this._chain = chain;
    this.pattern = pattern;
  }
  get isChainStart() {
    return this._chain.parts[0] === this;
  }
  get isHidden() {
    return this._hidden;
  }
  hidden(hidden = true) {
    this._hidden = hidden;
    return this;
  }
  matches(inputString, context, withRawMatches = false) {
    const result = this.pattern.matches(inputString, context, true);
    let [allMatches, rawMatches] = result;
    allMatches = this._truncateRepeater(allMatches, inputString);
    rawMatches = this._truncateRepeater(rawMatches, inputString);
    this._validateRepeater(rawMatches);
    if (withRawMatches) return [allMatches, rawMatches];
    return allMatches;
  }
  _truncateRepeater(matches, inputString) {
    if (!matches.length) return matches;
    if (!this.isChainStart) {
      const separator = inputString.slice(0, matches[0].initiator.rawStart);
      if (separator) return [];
    }
    let j = 1;
    for (let i = 0; i < matches.length - 1; i++) {
      const sep = inputString.slice(matches[i].initiator.rawEnd, matches[i + 1].initiator.rawStart);
      if (sep) break;
      j++;
    }
    let truncated = matches.slice(0, j);
    if (this.repeaterEnd !== null) {
      truncated = truncated.filter((m) => m.matchIndex < this.repeaterEnd);
    }
    return truncated;
  }
  _validateRepeater(matches) {
    const maxMatchIndex = matches.length > 0 ? Math.max(...matches.map((m) => m.matchIndex)) : -1;
    if (maxMatchIndex + 1 < this.repeaterStart) {
      throw new InvalidChainException();
    }
  }
  repeater(value) {
    if (typeof value === "number") {
      this.repeaterStart = value;
      this.repeaterEnd = value;
      return this;
    }
    const v = String(value);
    if (v === "+") {
      this.repeaterStart = 1;
      this.repeaterEnd = null;
    } else if (v === "*") {
      this.repeaterStart = 0;
      this.repeaterEnd = null;
    } else if (v === "?") {
      this.repeaterStart = 0;
      this.repeaterEnd = 1;
    } else {
      const m = /\{\s*(\d*)\s*,?\s*(\d*)\s*\}/.exec(v);
      if (m) {
        this.repeaterStart = m[1] ? parseInt(m[1]) : 0;
        this.repeaterEnd = m[2] ? parseInt(m[2]) : null;
      }
    }
    return this;
  }
  // Proxy chain methods for fluent API
  regex(opts, ...rest) {
    return this._chain.regex(opts, ...rest);
  }
  string(opts, ...rest) {
    return this._chain.string(opts, ...rest);
  }
  functional(opts, ...fns) {
    return this._chain.functional(opts, ...fns);
  }
  chain(opts) {
    return this._chain.chain(opts);
  }
  close() {
    return this._chain.close();
  }
  toString() {
    return `${this.pattern}({${this.repeaterStart},${this.repeaterEnd}})`;
  }
}
class Chain extends Pattern {
  parts = [];
  // Builder-compatible defaults (populated by Builder.chain())
  _defaults = {};
  _regexDefaults = {};
  _stringDefaults = {};
  _functionalDefaults = {};
  _chainDefaults = {};
  _parent;
  _chainBreaker;
  _matchKwargs;
  constructor(parent, opts = {}) {
    super(opts);
    this._parent = parent;
    this._matchKwargs = filterMatchKwargs(opts);
    this._chainBreaker = opts.chainBreaker ?? null;
  }
  // Builder-like methods so chain().regex() etc. work
  defaults(kwargs) {
    setDefaults(kwargs, this._defaults, true);
    if ("children" in kwargs) this.children = kwargs.children;
    if ("privateParent" in kwargs) this.privateParent = kwargs.privateParent;
    if ("privateChildren" in kwargs) this.privateChildren = kwargs.privateChildren;
    if ("conflictSolver" in kwargs) this._matchKwargs.conflictSolver = kwargs.conflictSolver;
    if ("privateNames" in kwargs) this.privateNames = kwargs.privateNames;
    return this;
  }
  regexDefaults(kwargs) {
    setDefaults(kwargs, this._regexDefaults, true);
    return this;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  regex(...args) {
    const lastArg = args[args.length - 1];
    const opts = typeof lastArg === "object" && lastArg !== null && !Array.isArray(lastArg) ? lastArg : {};
    const patterns = typeof lastArg === "object" && lastArg !== null && !Array.isArray(lastArg) ? args.slice(0, -1) : args;
    const kwargs = { ...this._regexDefaults, ...this._defaults, ...opts };
    const pat = new RePattern(kwargs, ...patterns);
    return this.pattern(pat);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  string(...args) {
    const lastArg = args[args.length - 1];
    const opts = typeof lastArg === "object" && lastArg !== null && !Array.isArray(lastArg) ? lastArg : {};
    const patterns = typeof lastArg === "object" && lastArg !== null && !Array.isArray(lastArg) ? args.slice(0, -1) : args;
    const kwargs = { ...this._stringDefaults, ...this._defaults, ...opts };
    const pat = new StringPattern(kwargs, ...patterns);
    return this.pattern(pat);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  functional(...args) {
    const fn = args[0];
    const opts = args.length > 1 && typeof args[1] === "object" ? args[1] : {};
    const kwargs = { ...this._functionalDefaults, ...this._defaults, ...opts };
    const pat = new FunctionalPattern(kwargs, fn);
    return this.pattern(pat);
  }
  chain(opts = {}) {
    const kwargs = { ...this._chainDefaults, ...this._defaults, ...opts };
    const c = new Chain(this, kwargs);
    c._defaults = { ...this._defaults };
    c._regexDefaults = { ...this._regexDefaults };
    c._stringDefaults = { ...this._stringDefaults };
    c._functionalDefaults = { ...this._functionalDefaults };
    c._chainDefaults = { ...this._chainDefaults };
    this.pattern(c);
    return c;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pattern(...patterns) {
    if (!patterns.length) throw new Error("One pattern should be given to the chain");
    if (patterns.length > 1) throw new Error("Only one pattern can be given to the chain");
    const part = new ChainPart(this, patterns[0]);
    this.parts.push(part);
    return part;
  }
  close() {
    let p = this._parent;
    while (p instanceof Chain) p = p._parent;
    return p;
  }
  get patterns() {
    return [this];
  }
  get matchOptions() {
    return {};
  }
  /**
   * Override from Pattern — when the main chain match fails validation,
   * try removing trailing groups from the last pattern and re-validate.
   * Port of Python Chain._process_match fallback logic.
   */
  _processMatch(match, matchIndex, child = false) {
    const ret = super._processMatch(match, matchIndex, child);
    if (ret) return true;
    if (match.children.length > 0) {
      const lastPattern = match.children.get(match.children.length - 1).pattern;
      const lastPatternChildren = match.children.toArray().filter((c) => c.pattern === lastPattern);
      const lastPatternGroups = Chain._groupByMatchIndex(lastPatternChildren);
      if (lastPatternGroups.size > 0) {
        const originalChildren = new Matches(match.children.toArray());
        const originalEnd = match.end;
        const indices = [...lastPatternGroups.keys()].sort((a, b) => b - a);
        for (const idx of indices) {
          const lastMatches = lastPatternGroups.get(idx);
          for (const lm of lastMatches) {
            match.children.remove(lm);
          }
          match.end = match.children.length > 0 ? match.children.get(match.children.length - 1).end : match.start;
          const retried = super._processMatch(match, matchIndex, child);
          if (retried) return true;
        }
        match.children = originalChildren;
        match.end = originalEnd;
      }
    }
    return false;
  }
  *_match(_pattern, inputString, context) {
    let offset = 0;
    while (offset < inputString.length) {
      let chainFound = false;
      const currentChainMatches = [];
      let valid = true;
      let chainInputString = inputString.slice(offset);
      for (const chainPart of this.parts) {
        try {
          const result = chainPart.matches(chainInputString, context, true);
          const [partMatches, rawPartMatches] = result;
          Chain._fixMatchesOffset(partMatches, inputString, offset);
          Chain._fixMatchesOffset(rawPartMatches, inputString, offset);
          if (rawPartMatches.length > 0) {
            const groupedRaw = Chain._groupByMatchIndex(rawPartMatches);
            const groupedAll = Chain._groupByMatchIndex(partMatches);
            for (const [matchIndex, grouped] of groupedRaw) {
              chainFound = true;
              offset = grouped[grouped.length - 1].rawEnd;
              chainInputString = inputString.slice(offset);
              if (!chainPart.isHidden) {
                const groupedMatches = groupedAll.get(matchIndex) ?? [];
                if (this._chainBreakerEval([...currentChainMatches, ...groupedMatches])) {
                  currentChainMatches.push(...groupedMatches);
                }
              }
            }
          }
        } catch (e) {
          if (e instanceof InvalidChainException) {
            valid = false;
            if (currentChainMatches.length > 0) {
              offset = currentChainMatches[0].rawEnd;
            } else {
              offset++;
            }
            break;
          }
          throw e;
        }
      }
      if (!chainFound) break;
      if (currentChainMatches.length > 0 && valid) {
        yield this._buildChainMatch(currentChainMatches, inputString);
      }
    }
  }
  _chainBreakerEval(matches) {
    if (!this._chainBreaker) return true;
    return !this._chainBreaker(new Matches(matches));
  }
  _buildChainMatch(currentChainMatches, inputString) {
    let start = Infinity;
    let end = -Infinity;
    for (const m of currentChainMatches) {
      if (m.start < start) start = m.start;
      if (m.end > end) end = m.end;
    }
    const match = new Match(start, end, {
      ...this._matchKwargs,
      pattern: this,
      inputString
    });
    for (const chainMatch of currentChainMatches) {
      if (chainMatch.children.length > 0) {
        for (const child of chainMatch.children) {
          match.children.append(child);
        }
      }
      if (!match.children.includes(chainMatch)) {
        match.children.append(chainMatch);
        chainMatch.parent = match;
      }
    }
    return match;
  }
  static _fixMatchesOffset(matches, inputString, offset) {
    for (const m of matches) {
      if (m.inputString !== inputString) {
        m.inputString = inputString;
        const oldRawStart = m.rawStart;
        const oldRawEnd = m.rawEnd;
        m.start += offset;
        m.end += offset;
        m.rawStart = oldRawStart + offset;
        m.rawEnd = oldRawEnd + offset;
      }
      if (m.children.length > 0) {
        Chain._fixMatchesOffset(m.children.toArray(), inputString, offset);
      }
    }
  }
  static _groupByMatchIndex(matches) {
    const map = /* @__PURE__ */ new Map();
    for (const m of matches) {
      const arr = map.get(m.matchIndex) ?? [];
      arr.push(m);
      map.set(m.matchIndex, arr);
    }
    return map;
  }
}
registerChain(Chain);
class Rebulk extends Builder {
  _patterns = [];
  _rules;
  _rebulks = [];
  _disabled;
  /** Optional property customizer (guessit-specific). */
  customizeProperties;
  constructor(opts = {}) {
    super();
    const d = opts.disabled;
    if (typeof d === "function") {
      this._disabled = d;
    } else if (typeof d === "boolean") {
      this._disabled = () => d;
    } else {
      this._disabled = () => false;
    }
    this._rules = new Rules();
    if (opts.defaultRules !== false) {
      this._rules.load(ConflictSolver, PrivateRemover);
    }
  }
  pattern(...patterns) {
    this._patterns.push(...patterns);
    return this;
  }
  rules(...rules) {
    this._rules.load(...rules);
    return this;
  }
  rebulk(...rebulks) {
    this._rebulks.push(...rebulks);
    return this;
  }
  /** Run all patterns and rules against `string`. */
  matches(string, context = {}) {
    const matches = new Matches(void 0, string);
    this._matchesPatterns(matches, context);
    this._executeRules(matches, context);
    return matches;
  }
  effectiveRules(context) {
    const rules = new Rules();
    rules.extend(this._rules);
    for (const child of this._rebulks) {
      if (!child._disabled(context ?? {})) {
        rules.extend(child._rules);
      }
    }
    return rules;
  }
  effectivePatterns(context) {
    const patterns = [...this._patterns];
    for (const child of this._rebulks) {
      if (!child._disabled(context ?? {})) {
        extendSafe(patterns, child._patterns);
      }
    }
    return patterns;
  }
  _executeRules(matches, context) {
    if (!this._disabled(context)) {
      const rules = this.effectiveRules(context);
      rules.executeAllRules(matches, context);
    }
  }
  _matchesPatterns(matches, context) {
    if (this._disabled(context)) return;
    const patterns = this.effectivePatterns(context);
    for (const pat of patterns) {
      if (pat.disabled && pat.disabled(context)) continue;
      const patternMatches = pat.matches(matches.inputString, context);
      for (const m of patternMatches) {
        if (m.marker) {
          matches.markers.append(m);
        } else {
          matches.append(m);
        }
      }
    }
  }
}
class Description {
}
class PatternDescription extends Description {
  pattern;
  _properties;
  constructor(pattern) {
    super();
    this.pattern = pattern;
    this._properties = {};
    const pat = pattern;
    if (pat.properties_ && Object.keys(pat.properties_).length > 0) {
      for (const [key, values] of Object.entries(pat.properties_)) {
        if (!this._properties[key]) this._properties[key] = [];
        extendSafe(this._properties[key], values);
      }
    } else if (pat.defaultValue !== void 0 && pat.defaultValue !== null) {
      const name = pat.name ?? "";
      if (!this._properties[name]) this._properties[name] = [];
      this._properties[name].push(pat.defaultValue);
    } else if (pattern instanceof StringPattern) {
      const name = pat.name ?? "";
      if (!this._properties[name]) this._properties[name] = [];
      extendSafe(this._properties[name], pat.patterns);
    } else if (pattern instanceof RePattern) {
      if (pat.name && !(pat.privateNames || []).includes(pat.name)) {
        if (!this._properties[pat.name]) this._properties[pat.name] = [];
        extendSafe(this._properties[pat.name], [null]);
      }
      if (!pat.privateChildren) {
        for (const regex of pat.patterns) {
          if (regex.source) {
            const groupRegex = /\(\?<([^>]+)>/g;
            let m;
            while ((m = groupRegex.exec(regex.source)) !== null) {
              const groupName = m[1];
              if (!(pat.privateNames || []).includes(groupName)) {
                if (!this._properties[groupName]) this._properties[groupName] = [];
                extendSafe(this._properties[groupName], [null]);
              }
            }
          }
        }
      }
    } else if (pattern instanceof FunctionalPattern) {
      if (pat.name && !(pat.privateNames || []).includes(pat.name)) {
        if (!this._properties[pat.name]) this._properties[pat.name] = [];
        extendSafe(this._properties[pat.name], [null]);
      }
    }
  }
  get properties() {
    return this._properties;
  }
}
class RuleDescription extends Description {
  rule;
  _properties;
  constructor(rule) {
    super();
    this.rule = rule;
    this._properties = {};
    const ruleProps = rule.constructor.properties;
    if (ruleProps) {
      for (const [key, values] of Object.entries(ruleProps)) {
        if (!this._properties[key]) this._properties[key] = [];
        extendSafe(this._properties[key], values);
      }
    }
  }
  get properties() {
    return this._properties;
  }
}
class Introspection extends Description {
  patterns;
  rules;
  constructor(rebulk, context) {
    super();
    const effectivePatterns = rebulk.effectivePatterns(context);
    this.patterns = effectivePatterns.filter((p) => !p.private_ && !p.marker).map((p) => new PatternDescription(p));
    const effectiveRules = rebulk.effectiveRules(context);
    this.rules = [];
    for (const rule of effectiveRules) {
      this.rules.push(new RuleDescription(rule));
    }
  }
  get properties() {
    const props = {};
    for (const pattern of this.patterns) {
      for (const [key, values] of Object.entries(pattern.properties)) {
        if (!props[key]) props[key] = [];
        extendSafe(props[key], values);
      }
    }
    for (const rule of this.rules) {
      for (const [key, values] of Object.entries(rule.properties)) {
        if (!props[key]) props[key] = [];
        extendSafe(props[key], values);
      }
    }
    return props;
  }
}
function introspect(rebulk, context) {
  return new Introspection(rebulk, context);
}
export {
  AppendMatch,
  AppendTags,
  BasePattern,
  Builder,
  Chain,
  ChainPart,
  Condition,
  ConflictSolver,
  Consequence,
  CustomRule,
  CyclicDependency,
  DEBUG,
  Description,
  FunctionalPattern,
  IdentitySet,
  Introspection,
  LOG_LEVEL,
  Markers,
  Match,
  Matches,
  MatchesDict,
  POST_PROCESS,
  PRE_PROCESS,
  Pattern,
  PatternDescription,
  PrivateRemover,
  RePattern,
  Rebulk,
  RemoveMatch,
  RemoveTags,
  RenameMatch,
  Rule,
  RuleDescription,
  Rules,
  StringPattern,
  alwaysTrue,
  call,
  charsAfter,
  charsBefore,
  charsSurround,
  defaultFormatter,
  definedAt,
  ensureDict,
  ensureList,
  executeRule,
  extendSafe,
  filterMatchKwargs,
  findAll,
  formatters,
  frameRepr,
  introspect,
  isIterable,
  setDebug,
  setLogLevel,
  toposort,
  toposortFlatten,
  toposortRules,
  validators
};
//# sourceMappingURL=rebulk-js.js.map
